import { version as appVersion } from "../package.json";
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import DeckGL from "@deck.gl/react";
import type { Layer } from "@deck.gl/core";
import { Map as MapLibreMap } from "react-map-gl/maplibre";
import maplibregl from "maplibre-gl";
import type { FeatureCollection, Geometry, LineString, Point, Polygon, MultiPolygon } from "geojson";
import { CHULA, SOURCE_CATALOG } from "@chula/shared";
import type {
  AcademicSnapshot,
  AirDashStation,
  AirQualityPoint,
  BangkokFloodWatch,
  CampusFloodEvent,
  CampusFloodStation,
  CampusZoneProperties,
  ExecutiveSnapshot,
  IncidentFeature,
  IntelligenceItem,
  PrecipNowcast,
  RainfallStation,
  TrafficCorridor,
  WaterGauge,
  WeatherSnapshot,
} from "@chula/shared";

import { useFeed } from "./hooks/useFeed";
import { useShuttlePositions } from "./hooks/useShuttlePositions";
import type { ShuttleLines } from "@chula/shared/shuttleSim";
import {
  type AqHeatmapPoint,
  bmaAqStationsLayer,
  buildingsLayer,
  campusBoundaryLayer,
  cctvLayer,
  cctvFrameLayer,
  drainageLineLayer,
  drainagePathLayer,
  declutterUndergroundLabels,
  electricityPathLayer,
  floodStationsLayer,
  waterPathLayer,
  drainageNodeLayer,
  electricityLineLayer,
  electricityNodeLayer,
  incidentLayer,
  cityReportHeatmapLayer,
  shuttleRouteLineLayer,
  shuttleRoutesLayer,
  shuttleStopsLayer,
  shuttleVehiclesLayer,
  transitStationsLayer,
  transitLinesLayer,
  campusGatesLayer,
  campusTreesLayer,
  woodPolygonsLayer,
  waterLineLayer,
  waterNodeLayer,
  // Pratumwan X-Ray
  pratumwanBoundaryLayer,
  pratumwanGroundPlane,
  pratumwanMetroTunnelLayer,
  pratumwanMetroStationsLayer,
  pratumwanWaterLayer,
  pratumwanSewerLayer,
  pratumwanStormLayer,
  pratumwanMeaHvLayer,
  type StationFeatureProps,
  type AqStation,
  type BuildingProperties,
  type CctvCamera,
  type ShuttleVehicle,
  type RouteProps,
  type StopProps,
  type StationProps,
  type TransitLineProps,
  type GateProps,
  type TreeProps,
  // Operations (modeled)
  parkingCapacityLayer,
  buildingOccupancyLayer,
  internalTrafficLayer,
  wifiSignalLayer,
  fireHoseLayer,
  trashCollectionLayer,
  type ParkingCapacityProps,
  type OccupancyProps,
  type InternalTrafficProps,
  type WifiSignalProps,
  type FireHoseProps,
  type TrashProps,
  type OpsClock,
} from "./map/layers";
import { modeledParkingOccupancy } from "./lib/parking";
import { occupiedNow } from "./lib/opsModel";
import { simNow, isSimClockPinned } from "./lib/simClock";
import { LENSES, type LayerId, type LensId, type BaseMapId } from "./map/presets";

import { TopBar } from "./components/TopBar";
import { IssueRoll } from "./components/IssueRoll";
import { LayerPalette } from "./components/LayerPalette";
import { NewsDesk } from "./components/NewsDesk";
import { useAdapterHealth } from "./components/AdapterHealthPanel";
import { getBuildingAttributes } from "./data/buildingAttributes";
import { BuildingCard } from "./components/BuildingCard";
import { IncidentCard } from "./components/IncidentCard";
import { BuildingSearch } from "./components/BuildingSearch";
import { HostStrip } from "./components/HostStrip";
import { LeftRail, type LeftRailCtx } from "./components/LeftRail";
import { type AqiTrend } from "./components/AqiBadge";

// Five views the operator opens from the top bar's MORE menu or via a map
// click. None of them are needed on first paint — lazy-load them so the
// landing bundle stays small. The 5 separate chunks add up to ~25 KB gz
// when finally fetched.
const SourceCatalog = lazy(() =>
  import("./components/SourceCatalog").then((m) => ({ default: m.SourceCatalog })),
);
const Manual = lazy(() =>
  import("./components/Manual").then((m) => ({ default: m.Manual })),
);
const SheetsPanel = lazy(() =>
  import("./components/SheetsPanel").then((m) => ({ default: m.SheetsPanel })),
);

// Tiny localStorage helper for sheets URL — the lazy import above is
// for the admin panel only; the URL is read on every render so it lives
// outside the lazy chunk.
const loadSheetsUrl = (): string => {
  try {
    return localStorage.getItem("chula:sheets-url-v1")
      ?? ((import.meta.env.VITE_SHEETS_URL as string | undefined) ?? "");
  } catch {
    return "";
  }
};
const AnalyticsPage = lazy(() =>
  import("./components/AnalyticsPage").then((m) => ({ default: m.AnalyticsPage })),
);
const CameraOperations = lazy(() => import("./components/CameraOperations").then(m => ({ default: m.CameraOperations })));
const CctvStreamModal = lazy(() =>
  import("./components/CctvStreamModal").then((m) => ({ default: m.CctvStreamModal })),
);
import { NewsTicker } from "./components/NewsTicker";
import { MobileNav, type MobilePanel } from "./components/MobileNav";
import { ChatBox } from "./components/ChatBox";
import { readingFor, campusSnapshot, type BuildingEnergyReading } from "./lib/energyModel";
import { AttentionQueue } from "./components/AttentionQueue";
import { OperatorRail, type DeskView } from "./components/OperatorRail";
import { StatusBadge } from "./components/StatusBadge";
import { deriveAlertStatus } from "./lib/alertStatus";
import { motionDuration } from "./lib/motion";
import { LiveIndicator } from "./components/LiveIndicator";
import { useAlerts } from "./hooks/useAlerts";
import { subscribeLive } from "./hooks/useLiveStream";
import { useConnectors } from "./hooks/useConnectors";
import { connectorLayerEntries, createConnectorLayer, formatConnectorTooltip } from "./map/connectorLayer";
import { MapLegend } from "./components/MapLegend";
import { useIsMobile } from "./hooks/useMediaQuery";
import { useOnlineStatus } from "./hooks/useOnlineStatus";
import { useTheme } from "./hooks/useTheme";
import { API_BASE } from "./lib/apiBase";
import { mapLabelObstacles } from "./map/parkingLabels";
import { makeBasemap } from "./map/basemap";
import { MapProvenance } from "./components/MapProvenance";

// Chat can target a different backend than the data API. On Cloudflare Pages the
// data API (chula-api) can't run the LLM, so the cloud build points chat at the
// NIM-enabled worker. Falls back to API_BASE (same-origin) for the self-hosted build.
const CHAT_API_BASE = (import.meta.env.VITE_CHAT_API_BASE_URL as string | undefined) ?? API_BASE;

function normalizeProperties<T extends { features?: Array<{ properties?: object | null }> }>(data: T): T {
  if (data && Array.isArray(data.features)) {
    for (const f of data.features) {
      if (f.properties === null || f.properties === undefined) {
        (f as Record<string, unknown>).properties = {};
      }
    }
  }
  return data;
}

function useGeoJson<T extends { features?: Array<{ properties?: object | null }> }>(
  path: string,
  enabled: boolean = true,
) {
  const [data, setData] = useState<T | null>(null);
  useEffect(() => {
    if (!enabled) {
      setData(null);
      return;
    }
    const ctrl = new AbortController();
    fetch(path, { signal: ctrl.signal })
      .then((r) => r.json())
      .then((j) => {
        setData(normalizeProperties(j as T));
      })
      .catch(() => {});
    return () => {
      ctrl.abort();
    };
  }, [path, enabled]);
  return data;
}


/** Academic-calendar tempo → headcount multiplier for the occupancy model (1 = semester in session). */
const TEMPO_MULTIPLIER: Record<"low" | "normal" | "high" | "peak", number> = { low: 0.4, normal: 1, high: 1.15, peak: 1.3 };

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export default function App() {
  // Lens + per-layer toggles. Hoisted ABOVE the useGeoJson calls so the
  // large GeoJSON files can gate their fetch on enabledLayers (H6 fix:
  // the campus-trees file alone is
  // 1.4 MB — fetching them on first paint regardless of which lens the
  // user opened was a real cost).
  const initialLens = ((): LensId => {
    if (typeof window === "undefined") return "ioc";
    const q = new URLSearchParams(window.location.search).get("lens");
    if (q === "mob") return "mobility";
    if (q && LENSES.some((l) => l.id === q)) return q as LensId;
    return "ioc";
  })();
  const [lens, setLens] = useState<LensId>(initialLens);
  const [enabledLayers, setEnabledLayers] = useState<Set<LayerId>>(
    () => new Set(LENSES.find((l) => l.id === initialLens)?.layers ?? LENSES[0]?.layers ?? []),
  );

  // View mode cycles 2D → 3D (buildings extrude) → 3DS (substructure: buildings
  // turn translucent, utilities drop to their burial depth). Camera follows.
  // Declared here (early) because the geo fetch gates + basemap style below
  // both depend on it. Default is extruded 3D — the BKKx city fabric.
  type ViewMode = "2D" | "3D" | "3DS";
  const [viewMode, setViewMode] = useState<ViewMode>(initialLens === "xray" ? "3DS" : "3D");
  const is3D = viewMode === "3D" || viewMode === "3DS";
  const isSubstructure = viewMode === "3DS";

  // Large GeoJSON files are gated on enabledLayers so they only fetch when
  // a layer that uses them is on. Campus outline / buildings / gates / cu-lands
  // stay always-on — they're part of every lens.
  const campus = useGeoJson<FeatureCollection<Polygon | MultiPolygon, CampusZoneProperties>>(
    "/geo/chula-campus.geojson",
  );
  const buildings = useGeoJson<FeatureCollection<Polygon | MultiPolygon, BuildingProperties>>(
    "/geo/chula-buildings.geojson",
  );
  // Drivable POP Bus loops (built by scripts/build-shuttle-lines.mjs from the
  // published stop order over the road graph). One file feeds both the drawn
  // route lines and the fleet simulator, so a bus is always on the line you see.
  const shuttleRoutes = useGeoJson<FeatureCollection<LineString, RouteProps>>("/geo/cu-shuttle-lines.geojson");
  const shuttleLines = shuttleRoutes as unknown as ShuttleLines | null;
  const shuttleStops = useGeoJson<FeatureCollection<Point, StopProps>>(
    "/geo/cu-shuttle-stops.geojson",
    enabledLayers.has("cu-shuttle-stops"),
  );
  const transitStations = useGeoJson<FeatureCollection<Point, StationProps>>(
    "/geo/transit-stations.geojson",
    enabledLayers.has("transit-stations"),
  );
  const transitLines = useGeoJson<FeatureCollection<LineString, TransitLineProps>>(
    "/geo/transit-lines.geojson",
    enabledLayers.has("transit-lines"),
  );
  const campusGates = useGeoJson<FeatureCollection<Point, GateProps>>(
    "/geo/chula-gates.geojson",
  );
  // Campus trees — 1.4MB; gated on the "campus-trees" layer.
  const campusTrees = useGeoJson<FeatureCollection<Point, TreeProps>>(
    "/geo/campus-trees.geojson",
    enabledLayers.has("campus-trees"),
  );
  const campusWood = useGeoJson<FeatureCollection<Polygon, { id: string; name?: string | null }>>(
    "/geo/campus-wood-polygons.geojson",
    // Gate on its own toggle (was gated on campus-trees, so "Grove polygons"
    // alone drew nothing and said nothing).
    enabledLayers.has("campus-wood") || enabledLayers.has("campus-trees"),
  );
  // Underground utilities + WiFi survey (hand-authored, small files; the
  // show* flags in the render block below control visibility per layer).
  const electricityFc = useGeoJson<FeatureCollection<Geometry, Record<string, unknown>>>(
    "/geo/cu-electricity.geojson",
  );
  const waterFc = useGeoJson<FeatureCollection<Geometry, Record<string, unknown>>>(
    "/geo/cu-water.geojson",
  );
  const drainageFc = useGeoJson<FeatureCollection<Geometry, Record<string, unknown>>>(
    "/geo/cu-drainage.geojson",
  );
  // Picked building (card) — declared here because the occupancy fetch below is gated on it.
  const [selectedBuilding, setSelectedBuilding] = useState<BuildingProperties | null>(null);
  const selectedBuildingId = selectedBuilding?.id ?? null;
  // Operations — modeled from real geometry (scripts/build-ops-mock.mjs); scenario tier.
  const opsParking = useGeoJson<FeatureCollection<Polygon | MultiPolygon, ParkingCapacityProps>>("/geo/ops/parking-capacity.geojson", enabledLayers.has("parking-capacity"));
  const opsOccupancy = useGeoJson<FeatureCollection<Polygon | MultiPolygon, OccupancyProps>>("/geo/ops/building-occupancy.geojson", enabledLayers.has("building-occupancy") || selectedBuildingId != null);
  const opsTraffic = useGeoJson<FeatureCollection<LineString, InternalTrafficProps>>("/geo/ops/internal-traffic.geojson", enabledLayers.has("internal-traffic"));
  const opsWifi = useGeoJson<FeatureCollection<Point, WifiSignalProps>>("/geo/ops/wifi-signal.geojson", enabledLayers.has("wifi-signal"));
  const opsFire = useGeoJson<FeatureCollection<Point, FireHoseProps>>("/geo/ops/fire-hose.geojson", enabledLayers.has("fire-hose"));
  const opsTrash = useGeoJson<FeatureCollection<Point, TrashProps>>("/geo/ops/trash-collection.geojson", enabledLayers.has("trash-collection"));
  // Pratumwan X-Ray subsurface + overhead infrastructure (9 hand-authored
  // datasets, ground-truthed against MRTA/MWA/BMA/MEA references).
  const ptBoundary = useGeoJson<FeatureCollection<Polygon, Record<string, unknown>>>("/geo/pratumwan/district-boundary.geojson");
  const ptMetroTunnels = useGeoJson<FeatureCollection<LineString, Record<string, unknown>>>("/geo/pratumwan/metro-blue-tunnel.geojson");
  const ptMetroStations = useGeoJson<FeatureCollection<Polygon, Record<string, unknown>>>("/geo/pratumwan/metro-blue-stations.geojson");
  const ptWater = useGeoJson<FeatureCollection<Geometry, Record<string, unknown>>>("/geo/pratumwan/mwa-water-mains.geojson");
  const ptSewer = useGeoJson<FeatureCollection<Geometry, Record<string, unknown>>>("/geo/pratumwan/bma-sewers.geojson");
  const ptStorm = useGeoJson<FeatureCollection<Geometry, Record<string, unknown>>>("/geo/pratumwan/bma-storm.geojson");
  const ptMeaHv = useGeoJson<FeatureCollection<Geometry, Record<string, unknown>>>("/geo/pratumwan/mea-hv-underground.geojson");

  // Hour state for traffic simulation — auto-tracks wall clock (no idle slider).
  const [hour, setHour] = useState<number>(() => simNow().getHours() + simNow().getMinutes() / 60);
  useEffect(() => {
    const tick = () => setHour(simNow().getHours() + simNow().getMinutes() / 60);
    const id = setInterval(tick, 60_000);
    return () => clearInterval(id);
  }, []);

  // Lens + per-layer toggles + base map
  // (lens + enabledLayers are declared at the top of App so the GeoJSON
  //  fetches below can gate on them; see H6 fix comment)
  // Base map (street/topo/aerial/night/true-color). Mutually exclusive ground
  // layer that everything else stacks on top of. Persists across lens changes
  // unless a lens explicitly declares its preferred baseMap.
  const [baseMap, setBaseMap] = useState<BaseMapId>("streets");
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [camerasOpen, setCamerasOpen] = useState(()=>new URLSearchParams(location.search).has('vision'));
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [ghostMode, setGhostMode] = useState(false);
  const handleToggleGhostMode = useCallback(() => {
    setGhostMode((prev) => !prev);
  }, []);
  const [manualOpen, setManualOpen] = useState(false);
  const [sheetsOpen, setSheetsOpen] = useState(false);
  const [sheetsConfigured, setSheetsConfigured] = useState(() => Boolean(loadSheetsUrl()));

  const { theme } = useTheme();

  // Mobile: 1-column stack with bottom segmented nav to swap panels.
  const isMobile = useIsMobile(1100);
  const online = useOnlineStatus();
  const [mobilePanel, setMobilePanel] = useState<MobilePanel>("map");
  const [deskView, setDeskView] = useState<DeskView>("attention");

  const mapHostRef = useRef<HTMLDivElement>(null);
  const [mapSize, setMapSize] = useState({ width: 1, height: 1 });

  // Controlled map viewState — needed so building-search and click can fly the camera.
  const [viewState, setViewState] = useState({
    ...CHULA.defaultView,
    // District context is the briefing default: it keeps the full campus
    // legible while showing the surrounding live traffic corridors.
    zoom: 14.6,
    minZoom: 12,
    maxZoom: 20,
    transitionDuration: 0,
  });

  // Selected building for the popup card.
  // [lng, lat] of the picked/searched building — drives the card's Street View thumb.
  const [selectedCoord, setSelectedCoord] = useState<[number, number] | null>(null);
  const [selectedIncidentId, setSelectedIncidentId] = useState<string | null>(null);
  /** Sticky selection so ARCHIVE tickets stay open after leaving the live feed. */
  const [selectedIncident, setSelectedIncident] = useState<IncidentFeature | null>(null);
  const [selectedCctv, setSelectedCctv] = useState<CctvCamera | null>(null);
  /**
   * Deep link from the concierge bot (?report=CU-2026-0002): after the live
   * feed hydrates we look the ticket up and fly + open the card so the user
   * lands directly on their pin. See https://chula.nonarkara.org?report=...
   */
  const deepLinkTicketRef = useRef<string | null>(null);
  if (deepLinkTicketRef.current === null && typeof window !== "undefined") {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("report");
    if (t) deepLinkTicketRef.current = t.trim();
  }

  // Live cursor coordinate readout. Throttled to ~10Hz via a ref so we
  // don't trash every drag frame with a re-render storm — at 60Hz mouse
  // sampling that was rebuilding the deck.gl layer set on each pixel.
  const [cursorCoord, setCursorCoord] = useState<[number, number] | null>(null);
  const cursorLastCommitMs = useRef(0);

  const liveTrafficEnabled = enabledLayers.has("traffic-live");
  // NOTE: mapStyle is memoized further down, after viewMode/is3D exist —
  // the 3D building fabric is part of the maplibre style now.

  // Camera helpers
  const flyTo = useCallback((longitude: number, latitude: number, zoom = 17) => {
    setViewState((prev) => ({
      ...prev,
      longitude,
      latitude,
      zoom,
      transitionDuration: motionDuration(700),
    }));
  }, []);
  const handleMapClick = useCallback((info: { layer?: { id?: string } | null; object?: unknown; coordinate?: number[] }) => {
    if (info.layer?.id === "campus-buildings" && info.object) {
      const f = info.object as { properties: BuildingProperties; geometry: { coordinates: number[][][] | number[][][][] } };
      setSelectedBuilding(f.properties);
      const [lng, lat] = info.coordinate ?? [viewState.longitude, viewState.latitude];
      setSelectedCoord([lng, lat]);
      flyTo(lng, lat, Math.max(viewState.zoom, 17));
    } else if (
      (info.layer?.id === "incidents-city-reports" || info.layer?.id === "incidents-itic") &&
      info.object
    ) {
      // Incident dot click (Traffy CR or iTIC event) → open the issue card.
      const f = info.object as IncidentFeature;
      setSelectedIncidentId(f.id);
      setSelectedIncident(f);
      setSelectedBuilding(null);
      setSelectedCoord(null);
    } else if (info.layer?.id === "cctv-cameras" && info.object) {
      // Camera dot click → open the live stream modal.
      setSelectedCctv(info.object as CctvCamera);
      setSelectedBuilding(null);
      setSelectedCoord(null);
    } else {
      // Any click with no dedicated handler — empty ground, but also the
      // campus boundary fill, trees, gates, utility lines, ops fills — dismisses
      // whatever card is open. Previously only a click on nothing at all did,
      // so clicking "blank" campus (which is the pickable boundary polygon)
      // left the card stuck.
      setSelectedBuilding(null);
      setSelectedCoord(null);
      setSelectedIncidentId(null);
      setSelectedIncident(null);
      setSelectedCctv(null);
    }
  }, [flyTo, viewState.longitude, viewState.latitude, viewState.zoom]);

  const zoomBy = (delta: number) => {
    setViewState((prev) => ({
      ...prev,
      zoom: Math.max(prev.minZoom ?? 3, Math.min(prev.maxZoom ?? 20, prev.zoom + delta)),
      transitionDuration: motionDuration(200),
    }));
  };


  // (viewMode/is3D are declared early, near enabledLayers, because both the
  // geo fetch gates and the basemap style depend on them.)

  const mapStyle = useMemo(
    () => makeBasemap(
      theme === "light" ? "light" : theme === "high-contrast" ? "dark_matter" : "dark",
      liveTrafficEnabled,
      // Every non-carto ground (aerial/topo/night/true-color) is the style's
      // ground layer, not a deck.gl tile layer, so live traffic + labels
      // stay visible above it AND it's simply visible at any zoom — no
      // zoom-gated deck.gl visibility flag to silently hide it.
      baseMap,
      // 3D modes render the BKKx city fabric natively (PMTiles fill-extrusion,
      // ported from atlas.nonarkara.org) — replaces the deck.gl GeoJSON
      // fabric/skyline as the city-context layer. In 3DS the fabric stays off
      // so the underground X-ray reads against a quiet ground.
      is3D && !isSubstructure,
    ),
    [theme, liveTrafficEnabled, baseMap, is3D, isSubstructure],
  );

  // ── Energy model — synthetic per-building readings (stand-in for BEMS).
  // 5-min bucket inside readingFor() means values are stable for that
  // window, so the heatmap doesn't flicker between renders. We re-derive
  // every 5 min so anomalies move around realistically.
  const [energyBucket, setEnergyBucket] = useState(() => Math.floor(Date.now() / (5 * 60_000)));
  useEffect(() => {
    const id = setInterval(() => setEnergyBucket(Math.floor(Date.now() / (5 * 60_000))), 30_000);
    return () => clearInterval(id);
  }, []);
  const energyReadings = useMemo<BuildingEnergyReading[]>(() => {
    if (!buildings?.features) return [];
    return buildings.features.map((f) => readingFor(f));
  }, [buildings, energyBucket]);
  const energySnapshot = useMemo(
    () => campusSnapshot(energyReadings, { treeCount: campusTrees?.features?.length ?? 0 }),
    [energyReadings, campusTrees],
  );
  const energyByBuildingId = useMemo(() => {
    const m = new Map<string, { currentKw: number; vsAvg: number; anomalyScore: number }>();
    for (const r of energyReadings) m.set(r.buildingId, { currentKw: r.currentKw, vsAvg: r.vsAvg, anomalyScore: r.anomalyScore });
    return m;
  }, [energyReadings]);
  const energyMode = enabledLayers.has("campus-energy");

  const cycleViewMode = useCallback(() => {
    setViewMode((prevMode) => {
      return prevMode === "2D" ? "3D" : prevMode === "3D" ? "3DS" : "2D";
    });
  }, []);

  // Gate to disable onViewStateChange's bearing/pitch mirroring during our
  // own programmatic transitions (view-mode cycle). Without this, the very
  // first interpolated frame from deck.gl overwrites our target back to 0.
  const programmaticTransitionUntil = useRef(0);
  useEffect(() => {
    programmaticTransitionUntil.current = Date.now() + 900;  // 700ms anim + small buffer
    setViewState((prev) => ({
      ...prev,
      pitch: viewMode === "2D" ? 0 : viewMode === "3D" ? 50 : 62,
      bearing: viewMode === "2D" ? 0 : viewMode === "3D" ? -18 : -28,
      transitionDuration: motionDuration(700),
    }));
  }, [viewMode]);

  // Laptop equivalent of the two-finger twist gesture on a phone — 30° steps.
  const rotateBy = (deltaDeg: number) => {
    programmaticTransitionUntil.current = Date.now() + 400;
    setViewState((prev) => ({
      ...prev,
      bearing: prev.bearing + deltaDeg,
      transitionDuration: motionDuration(250),
    }));
  };

  // ESC closes the topmost overlay.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (manualOpen) setManualOpen(false);
      else if (analyticsOpen) setAnalyticsOpen(false);
      else if (catalogOpen) setCatalogOpen(false);
      else if (selectedCctv) setSelectedCctv(null);
      else if (selectedIncidentId) {
        setSelectedIncidentId(null);
        setSelectedIncident(null);
      }
      else if (selectedBuilding) { setSelectedBuilding(null); setSelectedCoord(null); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [catalogOpen, selectedBuilding, selectedIncidentId, manualOpen, analyticsOpen, selectedCctv]);

  // Lookup table for hover tooltips — keeps DeckGL declarative.
  const tooltipForPickMemo = useCallback((info: { layer?: { id?: string } | null; object?: unknown }) => {
    const o = info.object as Record<string, unknown> | undefined;
    if (!info.layer || !o) return null;
    const id = info.layer.id;
    if (id?.startsWith("connector:")) {
      const entry = connectorEntriesRef.current.find((e) => e.layerId === id);
      const props = (o as { properties?: Record<string, unknown> }).properties;
      return entry && props ? formatConnectorTooltip(props as never, entry) : null;
    }
    const p = (o as { properties?: Record<string, unknown> }).properties ?? o;
    const pick = (...keys: string[]) => {
      for (const k of keys) {
        const v = (p as Record<string, unknown>)[k];
        if (typeof v === "string" && v.trim()) return v;
        if (typeof v === "number") return String(v);
      }
      return null;
    };
    let title: string | null = null;
    let sub: string | null = null;
    switch (id) {
      case "campus-buildings":
        title = pick("nameEn", "name", "nameTh") ?? "Building";
        sub = (() => {
          const lvls = (p as { levels?: number }).levels;
          const h = (p as { height?: number }).height;
          if (h) return `${h} m tall`;
          if (lvls) return `${lvls} floors`;
          return null;
        })();
        break;
      case "campus-trees":
        title = pick("name", "species", "speciesTh", "genus") ?? "Tree";
        sub = (() => {
          const h = (p as { heightM?: number }).heightM;
          const c = (p as { crownRadiusM?: number }).crownRadiusM;
          const lt = (p as { leafType?: string }).leafType;
          const parts: string[] = [];
          if (h) parts.push(`${h} m tall`);
          if (c) parts.push(`canopy ${c} m radius`);
          if (lt) parts.push(lt);
          return parts.length ? parts.join(" · ") : "documented · OSM placeholder";
        })();
        break;
      case "incidents-itic":
      case "incidents-city-reports":
        title = pick("title") ?? "Incident";
        sub = pick("category", "severity", "status");
        break;
      case "bma-aq-stations":
        title = pick("name") ?? "AQ station";
        sub = `PM2.5 ${pick("pm25") ?? "—"} µg/m³`;
        break;
      case "cctv-cameras": {
        const cam = p as unknown as { distanceM?: number; organization?: string };
        title = pick("name") ?? "CCTV";
        // "380 m from campus" is the fact an operator needs — which gate to
        // walk to. The vendor name alone does not help them decide anything.
        sub = [
          typeof cam.distanceM === "number" ? `${cam.distanceM} m from campus` : null,
          cam.organization,
        ]
          .filter(Boolean)
          .join(" · ");
        break;
      }
      case "cu-shuttle-vehicles": {
        const v = p as unknown as ShuttleVehicle;
        title = `${v.id} · Line ${v.line}${shuttle.fallbackTier === "live" ? "" : " · SIM"}`;
        sub = v.atStop
          ? `at ${v.atStop} · ${v.occupancy ?? "—"} load`
          : `${v.speedKmh ?? "—"} km/h → ${v.nextStop ?? "next stop"} · ${v.occupancy ?? "—"} load`;
        break;
      }
      case "cu-shuttle-stops": {
        const lines = (p as { lines?: string[] }).lines ?? [];
        title = pick("name") ?? "Shuttle stop";
        sub = lines.length ? `Lines ${lines.join(", ")}` : "No line data";
        break;
      }
      case "parking-capacity": {
        const q = p as unknown as ParkingCapacityProps & { occupied?: number; pct?: number };
        title = q.name ?? (q.kind === "structure" ? "Parking structure" : "Surface lot");
        sub = `${q.occupied ?? "—"} / ${q.capacity} cars · ${q.pct ?? "—"} % · ${q.capacitySource === "osm-tag" ? "OSM capacity" : "modeled from area"} · SIM`;
        break;
      }
      case "building-occupancy": {
        const q = p as unknown as OccupancyProps & { occupied?: number; pct?: number };
        title = q.name ?? "Building";
        sub = `${q.occupied ?? "—"} / ${q.capacity} people · ${q.pct ?? "—"} % · ${q.use} · SIM`;
        break;
      }
      case "internal-traffic": {
        const q = p as unknown as InternalTrafficProps & { speedKmh?: number };
        title = q.name ?? `Campus ${q.highway}`;
        sub = `${q.speedKmh ?? "—"} km/h (free-flow ${q.freeFlowKmh})${q.gateApproach ? " · gate approach" : ""} · SIM`;
        break;
      }
      case "wifi-signal-aps": {
        const q = p as unknown as WifiSignalProps;
        title = q.building;
        sub = `${q.accessPoints} APs · ${q.signalDbm} dBm · ~${q.mbps} Mbps · from ${q.nearestSurvey} survey (${q.surveyDistM} m) · SIM`;
        break;
      }
      case "fire-hose": {
        const q = p as unknown as FireHoseProps;
        title = q.kind === "hydrant" ? `Hydrant · ${q.gate ?? ""}` : `Hose cabinet · ${q.building ?? ""}`;
        sub = `${q.status.toUpperCase()} · ${q.pressureBar} bar · inspected ${q.lastInspected}${q.floors ? ` · floors ${q.floors}` : ""} · SIM`;
        break;
      }
      case "trash-collection": {
        const q = p as unknown as TrashProps & { fillPct?: number };
        title = `${q.site} · ${q.binType}`;
        sub = `${q.fillPct ?? "—"} % full · ${q.litres} L · SIM`;
        break;
      }
      case "cu-shuttle-routes":
      case "cu-shuttle-line-1":
      case "cu-shuttle-line-2":
      case "cu-shuttle-line-3":
      case "cu-shuttle-line-4":
      case "cu-shuttle-line-5":
        title = pick("label") ?? "CU Shuttle";
        sub = pick("ref");
        break;
      case "transit-stations":
        title = pick("name") ?? "Transit station";
        sub = `${pick("system") ?? ""} · ${pick("line") ?? ""}`;
        break;
      case "cu-electricity-nodes":
        title = pick("name") ?? "Electricity";
        sub = (() => {
          const v = (p as { voltage?: number }).voltage;
          const c = (p as { capacityMva?: number; capacityKw?: number; capacityMwh?: number });
          const parts: string[] = [];
          if (v) parts.push(`${v} kV`);
          if (c.capacityMva) parts.push(`${c.capacityMva} MVA`);
          if (c.capacityKw) parts.push(`${c.capacityKw} kW PV`);
          if (c.capacityMwh) parts.push(`${c.capacityMwh} MWh BESS`);
          return parts.join(" · ");
        })();
        break;
      case "cu-electricity-lines":
      case "cu-electricity-paths-3ds":
        title = pick("name") ?? "Electricity line";
        sub = `${pick("voltage") ?? "—"} kV · buried ~2 m`;
        break;
      case "cu-water-nodes":
        title = pick("name") ?? "Water node";
        sub = pick("diameter") ? `Ø ${pick("diameter")} mm` : pick("kind");
        break;
      case "cu-water-lines":
      case "cu-water-paths-3ds":
        title = pick("name") ?? "Water pipe";
        sub = `Ø ${pick("diameter") ?? "—"} mm · buried ~3 m`;
        break;
      case "cu-drainage-nodes":
        title = pick("name") ?? "Drainage node";
        sub = (() => {
          const k = (p as { kind?: string }).kind;
          if (k === "retention-basin") return `Retention · ${pick("capacityM3") ?? "—"} m³`;
          return pick("describe");
        })();
        break;
      case "cu-drainage-lines":
      case "cu-drainage-paths-3ds":
        title = pick("name") ?? "Storm drain";
        sub = `Ø ${pick("diameter") ?? "—"} mm · buried ~4 m`;
        break;
      case "campus-boundary":
        title = "Chulalongkorn University campus";
        sub = "Boundary from OpenStreetMap · click a building for its card";
        break;
      case "campus-gates":
        title = pick("name") ?? "Campus gate";
        sub = pick("name") ? "Named gate / entrance" : "Minor entrance (unnamed in OSM)";
        break;
      case "transit-lines":
        title = pick("name") ?? pick("line") ?? "Rail line";
        sub = `${pick("system") ?? "BTS / MRT"} · alignment from OpenStreetMap`;
        break;
      case "pratumwan-metro-stations":
        title = pick("name") ?? "MRT station";
        sub = "MRT Blue Line · underground platform footprint";
        break;
      case "flood-gauges": {
        const s = p as unknown as CampusFloodStation;
        title = s.name ?? "Gauge station";
        sub = s.kind === "rain"
          ? `Rain ${s.rain24h != null ? Math.round(s.rain24h) : "—"} mm/24h · ${s.distanceKm} km`
          : `L${s.situationLevel ?? "—"}${s.levelMsl != null ? ` · ${s.levelMsl.toFixed(2)} m MSL` : ""} · ${s.distanceKm} km`;
        break;
      }
      default:
        return null;
    }
    return {
      html:
        `<div class="picker-tooltip"><div class="picker-title">${escapeHtml(title ?? "")}</div>` +
        (sub ? `<div class="picker-sub">${escapeHtml(sub)}</div>` : "") +
        `</div>`,
      style: {
        background: "transparent",
        border: "none",
        padding: "0",
      },
    };
  }, []);

  const onLensChange = useCallback((id: LensId) => {
    setLens(id);
    const next = LENSES.find((l) => l.id === id);
    if (next) {
      setEnabledLayers(new Set(next.layers));
      // A lens may carry its preferred base map (EXEC/ENV/VIB → aerial).
      // Picking the lens snaps both the layer set AND the ground style.
      if (next.baseMap) setBaseMap(next.baseMap);
    }
    // X-RAY is a cross-section — force 3DS so utilities sit under ghosted buildings.
    if (id === "xray") setViewMode("3DS");
    else setViewMode(previous => previous === "3DS" ? "3D" : previous);
    // Keep shareable deep links in sync (?lens=mobility → FLEET button / bookmarks).
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      if (id === "ioc") url.searchParams.delete("lens");
      else url.searchParams.set("lens", id);
      window.history.replaceState({}, "", url.toString());
    }
  }, []);

  const onOpenFleet = useCallback(() => {
    onLensChange("mobility");
    setViewMode("3D");
    setEnabledLayers((prev) => {
      const next = new Set(prev);
      next.add("cu-shuttle-1");
      next.add("cu-shuttle-2");
      next.add("cu-shuttle-3");
      next.add("cu-shuttle-4");
      next.add("cu-shuttle-5");
      next.add("cu-shuttle-stops");
      next.add("cu-shuttle-vehicles");
      next.add("cu-shuttle-routes");
      return next;
    });
  }, [onLensChange]);
  const onToggleLayer = useCallback((id: LayerId) => {
    setEnabledLayers((prev) => {
      const next = new Set(prev);
      const turningOn = !next.has(id);
      if (turningOn) next.add(id); else next.delete(id);
      return next;
    });
  }, []);
  const clearAllLayers = useCallback(() => setEnabledLayers(new Set()), []);

  // Feeds
  // 60s poll: Telegram campus reports should hit the map within a minute of
  // being sent to the group (server-side the feed cache is busted on ingest).
  // Fast poll — Telegram photos should appear on the map within ~15s of ingest.
  const cityReports = useFeed<IncidentFeature>(`${API_BASE}/api/incidents/city-reports`, 15_000);
  const iticEvents = useFeed<IncidentFeature>(`${API_BASE}/api/incidents/itic`, 3 * 60_000);

  /**
   * Deep link — when the concierge bot gives the user a URL like
   * https://chula.nonarkara.org?report=CU-2026-0002 we want the map to fly
   * to the pin and open the card as soon as the live feed hydrates. We try
   * the live feed first; if the ticket only exists in the archive (already
   * fixed) the campus-reports fallback fills it in. Either way, once the
   * incident is resolved we strip the query param so reloads don't replay.
   */
  useEffect(() => {
    const ticket = deepLinkTicketRef.current;
    if (!ticket) return;
    const fromFeed = cityReports.data.find(
      (r) => r.ticketNumber === ticket || r.ticketNumber === `CU-${ticket}` || r.id === ticket,
    );
    if (fromFeed) {
      setSelectedIncidentId(fromFeed.id);
      setSelectedIncident(fromFeed);
      if (Number.isFinite(fromFeed.lng) && Number.isFinite(fromFeed.lat)) {
        flyTo(fromFeed.lng, fromFeed.lat, 18);
      }
      const url = new URL(window.location.href);
      url.searchParams.delete("report");
      window.history.replaceState({}, "", url.toString());
      deepLinkTicketRef.current = null;
      return;
    }
    // Try the archive (closed) feed if the live feed didn't match.
    let cancelled = false;
    void fetch(`${API_BASE}/api/incidents/campus-reports?status=closed`)
      .then((r) => (r.ok ? r.json() : { reports: [] }))
      .then((json: { reports?: Array<{ id: string; ticket: string; lat: number | null; lng: number | null; text: string; category: string; severity: string; status: string; created_at: string; photo_file: string | null; sender: string; fixed_at: string | null; fixed_by: string | null }> }) => {
        if (cancelled) return;
        const hit = (json.reports ?? []).find(
          (r) => r.ticket === ticket || r.ticket === `CU-${ticket}` || r.id === ticket,
        );
        if (!hit || hit.lat === null || hit.lng === null) return;
        setSelectedIncidentId(hit.id);
        // /api/reports/photos/:file is public (the same Telegram photos are
        // already public in the reporting group), so the image URL is the
        // file path with no token — matches IssueRoll.
        const imageUrl = hit.photo_file
          ? `${API_BASE}/api/reports/photos/${hit.photo_file}`
          : undefined;
        const synthetic: IncidentFeature = {
          id: hit.id,
          ticketNumber: hit.ticket,
          lat: hit.lat,
          lng: hit.lng,
          category: (hit.category as IncidentFeature["category"]) || "other",
          severity: (hit.severity as IncidentFeature["severity"]) || "medium",
          status: hit.status === "fixed" || hit.status === "archived" ? "resolved" : "received",
          title: hit.text.slice(0, 80) || hit.category,
          description: hit.text,
          reportedAt: hit.created_at,
          resolvedAt: hit.fixed_at ?? undefined,
          imageUrl,
          reporterPlatform: "internal",
          nickname: hit.sender,
          staffName: hit.fixed_by ?? undefined,
        };
        setSelectedIncident(synthetic);
        flyTo(hit.lng, hit.lat, 18);
        const url = new URL(window.location.href);
        url.searchParams.delete("report");
        window.history.replaceState({}, "", url.toString());
        deepLinkTicketRef.current = null;
      })
      .catch(() => { /* deep link is best-effort */ });
    return () => { cancelled = true; };
  }, [cityReports.data, flyTo]);
  const news = useFeed<IntelligenceItem>(`${API_BASE}/api/news`, 3 * 60_000);
  const weather = useFeed<WeatherSnapshot>(`${API_BASE}/api/weather`, 30 * 60_000);
  const airQuality = useFeed<AirQualityPoint>(`${API_BASE}/api/air-quality`, 15 * 60_000);
  // Multi-station feed for the IQAir-style heatmap. Combines AQICN (when
  // AQICN_TOKEN is set) with the Open-Meteo Chula center anchor. Always
  // returns at least one point so the heatmap never disappears.
  const aqStations = useFeed<AqHeatmapPoint>(`${API_BASE}/api/air-quality/stations`, 15 * 60_000);
  const cctv = useFeed<CctvCamera>(`${API_BASE}/api/cctv/longdo`, 10 * 60_000);
  const shuttle = useFeed<ShuttleVehicle>(`${API_BASE}/api/transit/cu-shuttle`, 30_000);
  // Live GPS passes through untouched; a scenario-tier fleet is re-derived
  // locally from the same clock-driven engine the API used, so buses glide.
  const shuttleVehicles = useShuttlePositions(shuttle, shuttleLines);
  const aqiTrend = useFeed<AqiTrend>(`${API_BASE}/api/air-quality/trend`, 15 * 60_000);
  const executive = useFeed<ExecutiveSnapshot>(`${API_BASE}/api/executive`, 15 * 60_000);
  const precip = useFeed<PrecipNowcast>(`${API_BASE}/api/precip-nowcast`, 5 * 60_000);
  // Feeds only a lens rail renders poll only while that lens is showing
  // (LEFT_RAIL_SLOTS_BY_LENS). Campus flood stations also drive a map layer,
  // so they stay on whenever that layer is enabled too.
  const railShowsFlood = lens === "ioc" || lens === "maintenance" || lens === "mobility";
  const railShowsAirDash = lens === "maintenance" || lens === "mobility";
  const railShowsCorridors = lens === "mobility";
  const trafficCorridors = useFeed<TrafficCorridor>(`${API_BASE}/api/traffic/corridors`, 3 * 60_000, { enabled: railShowsCorridors });
  const academic = useFeed<AcademicSnapshot>(`${API_BASE}/api/academic-calendar`, 30 * 60_000);
  const floodWatch = useFeed<BangkokFloodWatch>(`${API_BASE}/api/flood/watch`, 5 * 60_000, { enabled: railShowsFlood });
  const floodGauges = useFeed<WaterGauge>(`${API_BASE}/api/flood/gauges`, 10 * 60_000, { enabled: railShowsFlood });
  const floodRain = useFeed<RainfallStation>(`${API_BASE}/api/flood/rain`, 10 * 60_000, { enabled: railShowsFlood });
  const floodCanals = useFeed<WaterGauge>(`${API_BASE}/api/flood/canals`, 10 * 60_000, { enabled: railShowsFlood });
  // Sister systems on this Mac: FloodDash (:8340) station-level detail near
  // campus + AirDash (:8341) nearest Air4Thai PM2.5 stations.
  const floodStations = useFeed<CampusFloodStation>(`${API_BASE}/api/flood/campus-stations`, 5 * 60_000, { enabled: railShowsFlood || enabledLayers.has("flood-gauges") });
  const floodEvents = useFeed<CampusFloodEvent>(`${API_BASE}/api/flood/campus-events`, 5 * 60_000, { enabled: railShowsFlood });
  const airDash = useFeed<AirDashStation>(`${API_BASE}/api/air-quality/airdash`, 10 * 60_000, { enabled: railShowsAirDash });
  const adapterHealth = useAdapterHealth(API_BASE, 30_000);
  const bangkokPulse = useMemo(() => {
    const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;
    const openReports = cityReports.data.filter((r) => r.status !== "resolved").length;
    const news24h = news.data.filter((n) => {
      const t = new Date(n.publishedAt).getTime();
      return !Number.isNaN(t) && t >= oneDayAgo;
    }).length;
    return {
      iticEvents: iticEvents.data.length,
      openReports,
      news24h,
      shuttleLive: shuttleVehicles.length,
    };
  }, [iticEvents.data, cityReports.data, news.data, shuttleVehicles.length]);

  // AQ station dots come from the live multi-station feed (AQICN + Open-Meteo
  // anchor) — the bundled BMA static snapshot that used to feed them went out
  // with the neighbourhood layers.
  const bmaAqStationList: AqStation[] = useMemo(
    () => aqStations.data.map((p, i) => ({
      id: `aq-${i}`, name: "AQ station", address: "", pm25: p.pm25, pm10: null, lng: p.lng, lat: p.lat,
    })),
    [aqStations.data],
  );

  // Bundle every feed + action the lens-scoped left rail needs. Memoized so
  // the rail's slot table (module-level constants) doesn't re-render on
  // unrelated shell re-renders — only when an actual input changes.
  const leftRailCtx = useMemo<LeftRailCtx>(
    () => ({
      apiBase: API_BASE,
      cctv,
      floodWatch,
      floodGauges,
      floodRain,
      floodCanals,
      floodStations,
      floodEvents,
      iticEvents,
      aqiTrend,
      airDash,
      trafficCorridors,
      energySnapshot,
      buildings,
      ghostMode,
      flyTo,
      cctvSamplingEnabled: !camerasOpen && (!isMobile || mobilePanel === "brief"),
      onSelectCctv: (camera) => { setMobilePanel("map"); setSelectedCctv(camera); },
      onOpenCameras: () => setCamerasOpen(true),
      onToggleGhostMode: handleToggleGhostMode,
    }),
    [camerasOpen, isMobile, mobilePanel, API_BASE, cctv, floodWatch, floodGauges, floodRain, floodCanals, floodStations, floodEvents, iticEvents, aqiTrend, airDash, trafficCorridors, energySnapshot, buildings, ghostMode, flyTo, handleToggleGhostMode],
  );

  // One clock object per minute for the modeled operations layers — every
  // time-varying value keys off it, so colours/labels move with the day but
  // never flicker between frames.
  const opsClock = useMemo<OpsClock>(() => {
    const now = simNow();
    return {
      now,
      minute: Math.floor(now.getTime() / 60_000),
      tempo: TEMPO_MULTIPLIER[academic.data[0]?.tempo ?? "normal"],
      parkingCurvePct: modeledParkingOccupancy(now).currentPct,
    };
    // `hour` re-ticks each minute (see setInterval above) and is the intended trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hour, academic.data]);

  // Quantize zoom for the layers memo: every zoom usage below is a threshold
  // comparison (`< 14`, GIBS visibility `< 10`, camera frames `>= 16`), so
  // integer precision is enough. Bucketing keeps the (expensive) layer array
  // from rebuilding on every animation frame of a zoom gesture.
  const zoomBucket = Math.round(viewState.zoom);

  // Stable handlers for the floating cards/search. Inline arrows here would get a
  // fresh reference every frame the parent re-renders during pan, defeating the
  // cards' React.memo and forcing them to reconcile on every gesture frame while open.
  const handleBuildingSearchSelect = useCallback(
    (centroid: [number, number], building: BuildingProperties) => {
      setSelectedBuilding(building);
      setSelectedCoord([centroid[0], centroid[1]]);
      flyTo(centroid[0], centroid[1], 17.5);
    },
    [flyTo],
  );
  // Modeled headcount for the open building card — same engine as the layer.
  const selectedOccupancy = useMemo(() => {
    if (!selectedBuildingId || !opsOccupancy) return null;
    const f = opsOccupancy.features.find((x) => x.properties.id === selectedBuildingId);
    if (!f) return null;
    const occupied = occupiedNow(f.properties.id, f.properties.capacity, f.properties.use, opsClock.now, opsClock.tempo ?? 1);
    return { occupied, capacity: f.properties.capacity, use: f.properties.use };
  }, [selectedBuildingId, opsOccupancy, opsClock]);
  const handleBuildingCardClose = useCallback(() => {
    setSelectedBuilding(null);
    setSelectedCoord(null);
  }, []);
  const handleIncidentCardClose = useCallback(() => {
    setSelectedIncidentId(null);
    setSelectedIncident(null);
  }, []);
  const handleIncidentReplied = useCallback(() => cityReports.refetch(), [cityReports.refetch]);
  const handleIssueSelect = useCallback((incident: IncidentFeature) => {
    setMobilePanel("map");
    setSelectedBuilding(null);
    setSelectedCoord(null);
    setSelectedIncidentId(incident.id);
    setSelectedIncident(incident);
  }, []);

  useEffect(() => {
    if (selectedBuildingId || selectedIncidentId || selectedCctv?.id) setMobilePanel("map");
  }, [selectedBuildingId, selectedIncidentId, selectedCctv?.id]);

  // ── v1: attention queue (rule-based alerts with owners and history) ──
  const alerts = useAlerts();
  const refetchAlerts = alerts.refetch;
  useEffect(() => subscribeLive((msg) => {
    if (msg.event === "alert" || msg.event === "resync") void refetchAlerts();
  }), [refetchAlerts]);
  const openIncidentFromAlert = useCallback((link: { id: string }) => {
    const hit = cityReports.data.find((r) => r.id === link.id || r.id.endsWith(link.id));
    if (hit) {
      handleIssueSelect(hit);
      if (Number.isFinite(hit.lng) && Number.isFinite(hit.lat)) flyTo(hit.lng, hit.lat, 18);
    }
  }, [cityReports.data, handleIssueSelect, flyTo]);

  // Top-bar verdict. Only a *confirmed* empty queue from a running engine may
  // say ALL CLEAR; loading or an unreachable engine reads as unknown.
  const alertStatus = useMemo(() => deriveAlertStatus(alerts), [alerts]);

  // ── v1: connected sources (declarative connectors, ADR 0001 §1) ──
  const [enabledConnectorLayers, setEnabledConnectorLayers] = useState<ReadonlySet<string>>(() => new Set());
  const activeConnectorIds = useMemo(
    () => Array.from(enabledConnectorLayers, (lid) => lid.replace(/^connector:/, "")),
    [enabledConnectorLayers],
  );
  const { registry: connectorRegistry, geojson: connectorGeojson } = useConnectors(activeConnectorIds);
  // Connector id → the label the connector gives itself. The queue shows this
  // instead of an internal id, so "campus-meters-demo" never reaches an
  // operator's screen.
  const connectorNames = useMemo(() => {
    const names = new Map<string, string>();
    for (const c of connectorRegistry.connectors) names.set(c.id, c.display?.label ?? c.name);
    return names;
  }, [connectorRegistry.connectors]);
  const connectorEntries = useMemo(() => connectorLayerEntries(connectorRegistry.connectors), [connectorRegistry.connectors]);
  const connectorEntriesRef = useRef(connectorEntries);
  connectorEntriesRef.current = connectorEntries;
  const toggleConnectorLayer = useCallback((layerId: string) => {
    setEnabledConnectorLayers((prev) => {
      const next = new Set(prev);
      if (next.has(layerId)) next.delete(layerId); else next.add(layerId);
      return next;
    });
  }, []);
  const emptyConnectorLayers = useMemo(() => {
    const out = new Set<string>();
    for (const lid of enabledConnectorLayers) {
      const g = connectorGeojson[lid.replace(/^connector:/, "")];
      if (g && !g.loading && (g.collection?.features.length ?? 0) === 0) out.add(lid);
    }
    return out;
  }, [enabledConnectorLayers, connectorGeojson]);
  const connectorDeckLayers = useMemo(() => {
    const out: Layer[] = [];
    for (const entry of connectorEntries) {
      if (!enabledConnectorLayers.has(entry.layerId)) continue;
      const fc = connectorGeojson[entry.connectorId]?.collection;
      if (fc) out.push(createConnectorLayer(entry, fc) as unknown as Layer);
    }
    return out;
  }, [connectorEntries, enabledConnectorLayers, connectorGeojson]);

  // Keep the open card in sync with live feed updates when the ticket is still open.
  useEffect(() => {
    if (!selectedIncidentId) return;
    const fresh =
      cityReports.data.find((r) => r.id === selectedIncidentId) ??
      iticEvents.data.find((r) => r.id === selectedIncidentId);
    if (fresh) setSelectedIncident(fresh);
  }, [cityReports.data, iticEvents.data, selectedIncidentId]);

  const layers = useMemo<Layer[]>(() => {
    const out: Layer[] = [];
    // Base map first — renders beneath all vector data + atmospheric overlays.
    // Every ground option (streets/aerial/topo/night/true-color) lives inside
    // the maplibre style now (see mapStyle above) — none of them are pushed
    // here as deck.gl layers, since deck.gl visibility flags previously gated
    // topo/night/true-color to low zoom levels the app never actually sits
    // at, which made picking them look like it did nothing.
    // Wood polygons paint canopy areas — must render BELOW individual trees
    if (enabledLayers.has("campus-wood") && campusWood)
      out.push(woodPolygonsLayer(campusWood) as Layer);
    if (enabledLayers.has("campus-trees") && campusTrees)
      out.push(campusTreesLayer(campusTrees, { extruded: is3D }) as Layer);
    if (enabledLayers.has("campus-boundary") && campus) out.push(campusBoundaryLayer(campus) as Layer);
    if (enabledLayers.has("campus-buildings") && buildings)
      out.push(buildingsLayer(buildings, {
        extruded: is3D,
        ghosted: isSubstructure,
        // Ghost-consumption highlighting needs the energy map even when the
        // campus-energy layer itself is off — without it every building
        // fades and nothing highlights.
        energyByBuildingId: energyMode || ghostMode ? energyByBuildingId : undefined,
        ghostConsumptionMode: ghostMode,
        dimFill: baseMap === "aerial",
      }) as Layer);
    if (enabledLayers.has("transit-lines") && transitLines)
      out.push(transitLinesLayer(transitLines) as Layer);
    if (enabledLayers.has("campus-gates") && campusGates)
      out.push(campusGatesLayer(campusGates) as Layer);
    // FloodDash gauge stations near campus — colored by HII situation level.
    if (enabledLayers.has("flood-gauges") && floodStations.data.length > 0)
      out.push(floodStationsLayer(floodStations.data) as Layer);
    if (enabledLayers.has("bma-aq-stations") && bmaAqStationList.length > 0) out.push(bmaAqStationsLayer(bmaAqStationList) as Layer);
    if (enabledLayers.has("cu-shuttle-routes") && shuttleRoutes) out.push(shuttleRoutesLayer(shuttleRoutes) as Layer);
    if (shuttleRoutes) {
      for (const id of ["1", "2", "3", "4", "5"] as const) {
        const key = `cu-shuttle-${id}` as LayerId;
        if (enabledLayers.has(key)) {
          const l = shuttleRouteLineLayer(id, shuttleRoutes);
          if (l) out.push(l as Layer);
        }
      }
    }
    if (enabledLayers.has("cu-shuttle-stops") && shuttleStops) out.push(shuttleStopsLayer(shuttleStops) as Layer);
    if (enabledLayers.has("transit-stations") && transitStations) out.push(transitStationsLayer(transitStations) as Layer);
    if (enabledLayers.has("cctv-cameras")) {
      out.push(cctvLayer(cctv.data) as Layer);
      const frames = cctvFrameLayer(cctv.data, zoomBucket, API_BASE);
      if (frames) out.push(frames as Layer);
    }
    if (enabledLayers.has("incidents-city-heatmap")) out.push(cityReportHeatmapLayer(cityReports.data) as Layer);
    if (enabledLayers.has("incidents-city-reports")) out.push(incidentLayer("incidents-city-reports", cityReports.data) as Layer);
    if (enabledLayers.has("incidents-itic")) out.push(incidentLayer("incidents-itic", iticEvents.data) as Layer);

    // Underground utilities. In 3DS we force-enable them and render the lines
    // as PathLayer at burial depth so they sit visibly under the ghosted
    // buildings. In 2D/3D the toggle controls visibility and lines stay flat.
    const showElectricity = enabledLayers.has("utility-electricity") || isSubstructure;
    const showWater = enabledLayers.has("utility-water") || isSubstructure;
    const showDrainage = enabledLayers.has("utility-drainage") || isSubstructure;
    if (showWater && waterFc) {
      if (isSubstructure) {
        const wp = waterPathLayer(waterFc);
        if (Array.isArray(wp)) out.push(...wp as Layer[]);
        else out.push(wp as Layer);
      } else out.push(waterLineLayer(waterFc) as Layer);
      out.push(waterNodeLayer(waterFc) as Layer);
    }
    if (showDrainage && drainageFc) {
      if (isSubstructure) {
        const dp = drainagePathLayer(drainageFc);
        if (Array.isArray(dp)) out.push(...dp as Layer[]);
        else out.push(dp as Layer);
      } else out.push(drainageLineLayer(drainageFc) as Layer);
      out.push(drainageNodeLayer(drainageFc) as Layer);
    }
    if (showElectricity && electricityFc) {
      if (isSubstructure) {
        const ep = electricityPathLayer(electricityFc);
        if (Array.isArray(ep)) out.push(...ep as Layer[]);
        else out.push(ep as Layer);
      } else out.push(electricityLineLayer(electricityFc) as Layer);
      out.push(electricityNodeLayer(electricityFc) as Layer);
    }

    // ── Pratumwan X-Ray — district-wide subsurface + overhead infrastructure ──
    // Ten datasets hand-authored against MRTA / MWA / BMA / MEA references.
    // All layers are gated on enabledLayers so the palette controls each independently.
    if (enabledLayers.has("pratumwan-boundary") && ptBoundary)
      out.push(pratumwanBoundaryLayer(ptBoundary) as Layer);
    if (enabledLayers.has("pratumwan-ground-plane") && ptBoundary)
      out.push(pratumwanGroundPlane([100.511, 13.726, 100.546, 13.754]) as Layer);
    if (enabledLayers.has("pratumwan-metro-tunnels") && ptMetroTunnels)
      for (const l of pratumwanMetroTunnelLayer(ptMetroTunnels)) out.push(l as Layer);
    if (enabledLayers.has("pratumwan-metro-stations") && ptMetroStations)
      for (const l of pratumwanMetroStationsLayer(ptMetroStations as unknown as FeatureCollection<Polygon, StationFeatureProps>)) out.push(l as Layer);
    if (enabledLayers.has("pratumwan-water") && ptWater)
      for (const l of pratumwanWaterLayer(ptWater)) out.push(l as Layer);
    if (enabledLayers.has("pratumwan-sewer") && ptSewer)
      for (const l of pratumwanSewerLayer(ptSewer)) out.push(l as Layer);
    if (enabledLayers.has("pratumwan-storm") && ptStorm)
      for (const l of pratumwanStormLayer(ptStorm)) out.push(l as Layer);
    if (enabledLayers.has("pratumwan-mea-hv") && ptMeaHv)
      for (const l of pratumwanMeaHvLayer(ptMeaHv)) out.push(l as Layer);

    // ── Operations (modeled, scenario tier) — above the fabric, below the fleet ──
    if (enabledLayers.has("internal-traffic") && opsTraffic) out.push(internalTrafficLayer(opsTraffic, opsClock) as Layer);
    if (enabledLayers.has("parking-capacity") && opsParking) out.push(...(parkingCapacityLayer(opsParking, opsClock, { ...viewState, ...mapSize, obstacles: mapLabelObstacles(mapHostRef.current) }) as Layer[]));
    if (enabledLayers.has("building-occupancy") && opsOccupancy) out.push(buildingOccupancyLayer(opsOccupancy, opsClock, { extruded: is3D }) as Layer);
    if (enabledLayers.has("fire-hose") && opsFire) out.push(fireHoseLayer(opsFire) as Layer);
    if (enabledLayers.has("trash-collection") && opsTrash) out.push(trashCollectionLayer(opsTrash, opsClock) as Layer);
    // WiFi sits at the top so it's never occluded by other layers
    if (enabledLayers.has("wifi-signal") && opsWifi) out.push(...(wifiSignalLayer(opsWifi) as Layer[]));

    // Counters belong above building fills; keep their depth independent so
    // modeled geometry cannot cover their text. Live fleet still wins hover.
    const parkingLabelsIndex = out.findIndex(layer => layer.id === "parking-capacity-labels");
    if (parkingLabelsIndex >= 0) out.push(...out.splice(parkingLabelsIndex, 1));

    // The moving fleet goes last — it is the one layer an operator tracks
    // second by second, and pushed earlier it drew beneath the modeled ops
    // fills, which both dimmed the buses and stole their tooltip on hover.
    if (enabledLayers.has("cu-shuttle-vehicles") && shuttleVehicles.length > 0) out.push(shuttleVehiclesLayer(shuttleVehicles) as Layer);

    return declutterUndergroundLabels(out, { ...viewState, ...mapSize }, mapLabelObstacles(mapHostRef.current));
  }, [
    enabledLayers, baseMap, is3D, isSubstructure, campus, buildings,
    shuttleRoutes, shuttleStops, transitStations, transitLines, campusGates,
    shuttleVehicles, cctv.data, cityReports.data,
    iticEvents.data, bmaAqStationList, floodStations.data, electricityFc, waterFc, drainageFc,
    energyMode, energyByBuildingId, ghostMode,
    campusTrees, campusWood,
    ptBoundary, ptMetroTunnels, ptMetroStations, ptWater, ptSewer, ptStorm, ptMeaHv,
    opsParking, opsOccupancy, opsTraffic, opsWifi, opsFire, opsTrash, opsClock, viewState, mapSize,
  ]);

  // Connected-source layers slot in under the moving fleet (the bus layer
  // stays last so it is never occluded and always wins the hover).
  const deckLayers = useMemo<Layer[]>(() => {
    if (connectorDeckLayers.length === 0) return layers;
    const busIdx = layers.findIndex((l) => l.id === "cu-shuttle-vehicles");
    if (busIdx < 0) return [...layers, ...connectorDeckLayers];
    return [...layers.slice(0, busIdx), ...connectorDeckLayers, ...layers.slice(busIdx)];
  }, [layers, connectorDeckLayers]);

  /**
   * Layers that are backed by a live feed rather than a shipped GeoJSON, and
   * whose feed currently holds zero features. Every render branch for these
   * is gated on the data being non-empty, so with an empty feed the toggle
   * flips on and draws nothing — the palette needs to say that out loud.
   *
   * Static-GeoJSON layers are deliberately absent here — those files ship
   * with the app and are never empty. The shuttle fleet is the one modeled
   * layer listed: outside service hours the simulator returns no buses.
   */
  const emptyLayers = useMemo(() => {
    const empty = new Set<LayerId>();
    const markIf = (isEmpty: boolean, ...ids: LayerId[]) => {
      if (isEmpty) for (const id of ids) empty.add(id);
    };
    markIf(cctv.data.length === 0, "cctv-cameras");
    markIf(cityReports.data.length === 0, "incidents-city-reports", "incidents-city-heatmap");
    markIf(iticEvents.data.length === 0, "incidents-itic");
    markIf(shuttleVehicles.length === 0, "cu-shuttle-vehicles"); // out of service hours → nothing to draw
    markIf(aqStations.data.length === 0, "bma-aq-stations");
    markIf(floodStations.data.length === 0, "flood-gauges");
    return empty;
  }, [
    cctv.data, cityReports.data, iticEvents.data,
    shuttleVehicles.length, aqStations.data, floodStations.data,
  ]);

  const feedHealth = useMemo(() => [
    { label: "NEWS", tier: news.fallbackTier, ageMinutes: news.ageMinutes },
    { label: "CR", tier: cityReports.fallbackTier, ageMinutes: cityReports.ageMinutes },
    { label: "AQ", tier: aqiTrend.fallbackTier, ageMinutes: aqiTrend.ageMinutes },
    { label: "WX", tier: weather.fallbackTier, ageMinutes: weather.ageMinutes },
    { label: "BUS", tier: shuttle.fallbackTier, ageMinutes: shuttle.ageMinutes },
  ], [news.fallbackTier, news.ageMinutes, cityReports.fallbackTier, cityReports.ageMinutes, aqiTrend.fallbackTier, aqiTrend.ageMinutes, weather.fallbackTier, weather.ageMinutes, shuttle.fallbackTier, shuttle.ageMinutes]);

  return (
    <div
      className={`shell ${isMobile ? `mobile mobile-panel-${mobilePanel}` : ""} ${lens === "xray" ? "xray-active" : ""}`}
      data-mobile={isMobile ? "true" : "false"}
    >
      {!online && (
        <div className="offline-banner mono" role="alert" aria-live="assertive">
          ⚠ OFFLINE — feeds are stale until the connection returns
        </div>
      )}
      {/* ── Top bar ── */}
      <TopBar
        statusBadge={
          <StatusBadge
            level={alertStatus.level}
            count={alerts.summary.total}
            label={alertStatus.label}
            detail={alertStatus.detail}
            onClick={() => {
              setDeskView("attention");
              if (isMobile) setMobilePanel("layers");
              requestAnimationFrame(() => document.querySelector<HTMLButtonElement>('[data-desk-tab="attention"]')?.focus());
            }}
          />
        }
        feeds={feedHealth}
        onOpenCatalog={() => setCatalogOpen(true)}
        catalogCount={SOURCE_CATALOG.length}
        viewMode={viewMode}
        onCycleViewMode={cycleViewMode}
        onSkipToMap={() => setMobilePanel("map")}
        onOpenManual={() => setManualOpen(true)}
        onOpenSheets={() => setSheetsOpen(true)}
        sheetsConfigured={sheetsConfigured}
        academic={academic.data[0] ?? null}
        adapterHealth={adapterHealth}
        onOpenAnalytics={() => setAnalyticsOpen(true)}
        onOpenCameras={() => setCamerasOpen(true)}
        onOpenFleet={onOpenFleet}
        fleetLiveCount={shuttle.fallbackTier === "live" ? shuttle.data.length : null}
      />

      {/* ── Host strip: Bangkok right now + campus pulse ── */}
      <HostStrip
        bangkokAqi={aqiTrend.data[0]?.current.aqi ?? null}
        bangkokPm25={aqiTrend.data[0]?.current.pm25 ?? null}
        weather={weather.data[0] ?? null}
        pulse={bangkokPulse}
        precipNowcast={precip.data[0] ?? null}
        shuttleTier={shuttle.fallbackTier}
        compact
      />
      {/* The operational headline banner was folded into the attention
          queue + top-bar status badge (design review 2026-09): three stacked
          strips of equal-weight numbers made "what needs me?" slower to read. */}

      {/* ── News ticker ── */}
      <NewsTicker items={news.data} loading={news.fallbackTier === "loading"} fallbackTier={news.fallbackTier} />

      {/* ── Left sidebar: operational or executive brief ──
          The rail itself is a small data table (LeftRail.tsx) that maps each
          lens to the list of panels it carries. Adding/removing a panel
          from a lens is a one-line config edit there. */}
      <LeftRail lens={lens} ctx={leftRailCtx} />

      {/* ── Map center — nothing overlaps this ── */}
      <main id="main" className="map-area" inert={isMobile && mobilePanel !== "map"}>
        <div className="map-host" ref={mapHostRef}>
          <DeckGL
            onResize={setMapSize}
            viewState={viewState}
            onViewStateChange={({ viewState: vs, interactionState }) => {
              const is = (interactionState ?? {}) as {
                isDragging?: boolean;
                isPanning?: boolean;
                isZooming?: boolean;
                isRotating?: boolean;
              };
              const userInteracting = !!(is.isDragging || is.isPanning || is.isZooming || is.isRotating);
              // Any direct user input cancels the programmatic gate so the
              // gesture takes over immediately instead of waiting out the
              // 900ms post-transition window.
              if (userInteracting && Date.now() < programmaticTransitionUntil.current) {
                programmaticTransitionUntil.current = 0;
              }
              const inProgrammatic = Date.now() < programmaticTransitionUntil.current;
              const { longitude, latitude, zoom, pitch, bearing } = vs as Record<string, number>;
              setViewState((prev) => ({
                ...prev,
                longitude, latitude, zoom,
                pitch: inProgrammatic ? prev.pitch : pitch,
                bearing: inProgrammatic ? prev.bearing : bearing,
                // Always clear transitionDuration when the user is gesturing
                // so their drag follows the cursor 1:1 rather than animating
                // toward a lingering target. Outside interactions we keep
                // whatever transition the programmatic setter put there.
                transitionDuration: userInteracting ? 0 : prev.transitionDuration,
              }));
            }}
            controller={{ dragRotate: true, touchRotate: true, inertia: true }}
            layers={deckLayers}
            getTooltip={tooltipForPickMemo}
            onClick={handleMapClick}
            onHover={(info) => {
              if (info.coordinate) {
                const now = performance.now();
                if (now - cursorLastCommitMs.current < 100) return; // ≤10 Hz
                cursorLastCommitMs.current = now;
                setCursorCoord([info.coordinate[0], info.coordinate[1]]);
              } else if (cursorCoord) {
                setCursorCoord(null);
              }
            }}
          >
            <MapLibreMap
              mapStyle={mapStyle}
              mapLib={maplibregl as unknown as typeof maplibregl}
              attributionControl={false}
              renderWorldCopies={false}
              onLoad={(e) => {
                if (import.meta.env?.DEV) {
                  (window as unknown as { __map?: unknown }).__map = e.target;
                }
              }}
            />
          </DeckGL>
          <BuildingSearch
            buildings={buildings}
            onSelect={handleBuildingSearchSelect}
          />
          {/* Map zoom controls — back-up for gesture / trackpad */}
          <div className="zoom-controls" aria-label="Map zoom controls">
            <button onClick={() => zoomBy(0.6)} aria-label="Zoom in" className="zoom-btn">+</button>
            <button onClick={() => zoomBy(-0.6)} aria-label="Zoom out" className="zoom-btn">−</button>
            {/* Rotate — the laptop equivalent of a two-finger twist on the
                phone. 30° steps; the compass button below snaps back north. */}
            <button onClick={() => rotateBy(-30)} aria-label="Rotate map counter-clockwise" title="Rotate map ↺" className="zoom-btn rotate-btn">↺</button>
            <button onClick={() => rotateBy(30)} aria-label="Rotate map clockwise" title="Rotate map ↻" className="zoom-btn rotate-btn">↻</button>
          </div>
          {/* Compass — needle stays glued to TRUE north as the camera rotates.
              Click to snap back to north-up (bearing 0). Tooltip shows the
              current bearing so it's also a heading readout. */}
          <button
            type="button"
            className="map-compass"
            onClick={() => {
              programmaticTransitionUntil.current = Date.now() + 600;
              setViewState((v) => ({ ...v, bearing: 0, transitionDuration: motionDuration(400) }));
            }}
            title={`Bearing ${Math.round(((viewState.bearing % 360) + 360) % 360)}° — click to face north`}
            aria-label={`Bearing ${Math.round(((viewState.bearing % 360) + 360) % 360)} degrees. Click to reset to north-up.`}
          >
            <svg
              viewBox="0 0 40 40"
              style={{ transform: `rotate(${-viewState.bearing}deg)` }}
              aria-hidden
            >
              <circle cx="20" cy="20" r="17" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.55" />
              {/* North half — accent / pink */}
              <polygon points="20,4 14,20 20,17 26,20" fill="var(--brand)" />
              {/* South half — muted */}
              <polygon points="20,36 14,20 20,23 26,20" fill="currentColor" opacity="0.55" />
              <text x="20" y="13" textAnchor="middle" fontSize="6" fontWeight="700" fill="var(--brand)" fontFamily="var(--font-mono)">N</text>
            </svg>
          </button>
          {cursorCoord && (
            <div className="map-coord mono" aria-live="off" role="status">
              <span className="map-coord-label">LAT</span>
              <span className="map-coord-val">{cursorCoord[1].toFixed(5)}</span>
              <span className="map-coord-label">LNG</span>
              <span className="map-coord-val">{cursorCoord[0].toFixed(5)}</span>
              <span className="map-coord-label">Z</span>
              <span className="map-coord-val">{viewState.zoom.toFixed(1)}</span>
            </div>
          )}
          <MapProvenance baseMap={baseMap} liveTraffic={liveTrafficEnabled} />
          <MapLegend initiallyCollapsed enabledLayers={enabledLayers} connectorLayers={connectorEntries.filter((e) => enabledConnectorLayers.has(e.layerId))} />
          <BuildingCard
            building={selectedBuilding}
            attrs={getBuildingAttributes(selectedBuilding?.id)}
            coord={selectedCoord}
            occupancy={selectedOccupancy}
            onClose={handleBuildingCardClose}
          />
          <IncidentCard
            incident={
              selectedIncident ??
              cityReports.data.find((r) => r.id === selectedIncidentId) ??
              iticEvents.data.find((r) => r.id === selectedIncidentId) ??
              null
            }
            onClose={handleIncidentCardClose}
            apiBase={API_BASE}
            onReplied={handleIncidentReplied}
          />
          {selectedCctv && (
            <Suspense fallback={null}>
              <CctvStreamModal
                camera={selectedCctv}
                onClose={() => setSelectedCctv(null)}
                apiBase={API_BASE}
              />
            </Suspense>
          )}
        </div>
      </main>

      {/* One task, one scroll region; unrelated content never gates a control. */}
      <OperatorRail view={deskView} onChange={setDeskView}
        attention={<AttentionQueue queue={alerts} sourceNames={connectorNames}
          onFocus={([lng, lat]) => flyTo(lng, lat, 17)} onOpenIncident={openIncidentFromAlert} />}
        news={<NewsDesk items={news.data} loading={news.fallbackTier === "loading"}
          ageMinutes={news.ageMinutes} onRefresh={news.refetch}
          fallbackTier={news.fallbackTier === "loading" ? undefined : news.fallbackTier}
          unavailable={news.fallbackTier === "unavailable"} />}
        map={<LayerPalette lens={lens} onLensChange={onLensChange}
          enabled={enabledLayers} onToggleLayer={onToggleLayer} onClearAllLayers={clearAllLayers}
          baseMap={baseMap} onBaseMapChange={setBaseMap} emptyLayers={emptyLayers}
          connectorLayers={connectorEntries} enabledConnectorLayers={enabledConnectorLayers}
          onToggleConnectorLayer={toggleConnectorLayer} emptyConnectorLayers={emptyConnectorLayers} />}
      />

      {/* ── Bottom bar: ident / issue roll / counts ── */}
      <div className="bottom-bar">
        <div className="bottom-ident">
          <span className="pill">v{appVersion}</span>
        </div>
        <IssueRoll
          reports={cityReports.data}
          selectedId={selectedIncidentId}
          onSelect={handleIssueSelect}
          onFlyTo={(lng, lat) => flyTo(lng, lat, 18)}
          apiBase={API_BASE}
        />
        <div className="bottom-stats">
          <LiveIndicator />
          <span className="mono">
            {cctv.data.length} CCTV ·{" "}
            {shuttle.fallbackTier === "live"
              ? `${shuttleVehicles.length} BUSES LIVE`
              : shuttleVehicles.length > 0
                ? `${shuttleVehicles.length} BUSES · SIM${isSimClockPinned ? " · DEMO CLOCK" : ""}`
                : `BUSES · OFF SERVICE${isSimClockPinned ? " · DEMO CLOCK" : ""}`}
          </span>
        </div>
      </div>

      {camerasOpen && <Suspense fallback={null}><CameraOperations cameras={cctv.data} apiBase={API_BASE} fallbackTier={cctv.fallbackTier} ageMinutes={cctv.ageMinutes} onClose={() => setCamerasOpen(false)} onLocate={camera => { setCamerasOpen(false); setMobilePanel("map"); flyTo(camera.lng, camera.lat); setSelectedCctv(camera); }} /></Suspense>}
      {analyticsOpen && (
        <Suspense fallback={null}>
          <AnalyticsPage
            onClose={() => setAnalyticsOpen(false)}
            executive={executive.data[0] ?? null}
            airQuality={airQuality.data[0] ?? null}
            execTier={executive.fallbackTier === "loading" ? undefined : executive.fallbackTier}
            aqTier={airQuality.fallbackTier === "loading" ? undefined : airQuality.fallbackTier}
          />
        </Suspense>
      )}
      <Suspense fallback={null}>
        {catalogOpen ? <SourceCatalog open onClose={() => setCatalogOpen(false)} adapterHealth={adapterHealth} connectors={connectorRegistry} /> : null}
        {manualOpen  ? <Manual       open onClose={() => setManualOpen(false)} /> : null}
        {sheetsOpen   ? <SheetsPanel
          open
          onClose={() => { setSheetsOpen(false); setSheetsConfigured(Boolean(loadSheetsUrl())); }}
        /> : null}
      </Suspense>
      <ChatBox apiBase={CHAT_API_BASE} />
      {isMobile && <MobileNav panel={mobilePanel} onChange={(panel) => { if (panel === "layers") setDeskView("map"); setMobilePanel(panel); }} />}
    </div>
  );
}
